/*
 * Copyright (c) 2026, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { DeviceFlag } from "./application-types"

/**
 * Separator used wherever multiple flags are represented as one string.
 *
 * Keep the URL, cache key, back link, and filter input on the same format so
 * flags serialize and parse consistently. Known flags do not contain commas.
 */
export const FLAG_SEPARATOR = ","

/**
 * Returns known flags that have not been selected.
 *
 * @param selected - Flags currently applied to the device list.
 * @returns The unselected flags in `DeviceFlag` declaration order.
 *
 * @example
 * availableDeviceFlags(["ROOTED"]).length // 11
 * availableDeviceFlags(["CUSTOM"]).length // 12
 */
export function availableDeviceFlags(selected: string[]): DeviceFlag[] {
    return Object.values(DeviceFlag).filter((flag) => !selected.includes(flag))
}

/**
 * Normalizes a flag to the format used by the API and known flags.
 *
 * @param raw - Raw flag text.
 * @returns The trimmed, upper-case flag, or an empty string for blank input.
 *
 * @example
 * normalizeDeviceFlag("  rooted ") // "ROOTED"
 */
function normalizeDeviceFlag(raw: string): string {
    return raw.trim().toUpperCase()
}

/**
 * Parses comma-separated flag input into normalized, unique flags.
 *
 * Blank values are discarded and duplicates are collapsed while preserving the
 * order of the first occurrence.
 *
 * @param raw - Raw flag text.
 * @returns Normalized flags in the order they were entered.
 *
 * @example
 * parseDeviceFlags(" rooted , emulator ") // ["ROOTED", "EMULATOR"]
 * parseDeviceFlags("rooted,ROOTED") // ["ROOTED"]
 * parseDeviceFlags(" , ") // []
 */
export function parseDeviceFlags(raw: string): string[] {
    const flags = raw.split(FLAG_SEPARATOR).map(normalizeDeviceFlag).filter(Boolean)

    return [...new Set(flags)]
}
